/**
 * Post-payment landing — "You're in" experience tailored to what was bought.
 *
 * Polls /api/checkout/status/{session_id} until Stripe confirms, then renders
 * a hero + confirmation + "next steps" that vary by `package_type`:
 *   • service           → single à-la-carte session (auto-booked)
 *   • package           → multi-session bundle
 *   • membership        → rolling monthly subscription
 *   • online_programme  → self-guided PDF download
 *
 * Reachable only through `Protected` in App.js so `user` is always present.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  BadgeCheck, CalendarCheck, CheckCircle2, ClipboardList, CreditCard,
  Download, Infinity as InfinityIcon, Loader2, MessageSquare, PackageOpen,
  PartyPopper, Sparkles, Star, Ticket, Video, XCircle,
} from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current++ < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch { setStatus("error"); }
    };
    poll();
  }, [sessionId]);

  if (status === "polling") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] fl-noise flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/app" className="mb-10 flex justify-center"><Logo variant="wordmark" size="lg" /></Link>
          <div className="fl-card p-10 text-center" data-testid="checkout-polling">
            <Loader2 className="text-[#f47a20] animate-spin mx-auto" size={44} />
            <div className="fl-heading text-2xl mt-5">Confirming payment…</div>
            <div className="text-sm text-zinc-400 mt-2">Stripe usually takes a couple of seconds. Hang tight — we'll take you to your programme as soon as this is done.</div>
          </div>
        </div>
      </div>
    );
  }

  if (status !== "paid") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] fl-noise flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/app" className="mb-10 flex justify-center"><Logo variant="wordmark" size="lg" /></Link>
          <div className="fl-card p-8 text-center" data-testid="checkout-failed">
            <XCircle className="text-red-500 mx-auto" size={48} />
            <div className="fl-heading text-2xl mt-4">Payment not confirmed</div>
            <div className="text-sm text-zinc-400 mt-2 leading-relaxed">
              {status === "timeout"
                ? "This is taking longer than expected. Stripe often confirms in the background — check your inbox in a minute. If nothing arrives, try the checkout again."
                : "We couldn't confirm your payment. If you were charged, please contact us and we'll sort it out."}
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <button className="fl-btn justify-center" onClick={() => nav("/app/packages")} data-testid="ck-retry">
                Back to packages
              </button>
              <Link to="/contact" className="fl-btn-ghost text-sm" data-testid="ck-contact">
                Get in touch
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <PaidLanding tx={tx} user={user} nav={nav} />;
}

// ---------- purchase-type driven content ----------
function buildContent(tx) {
  const type = tx?.package_type || "package";
  const auto = tx?.auto_booked_appointment;
  const features = tx?.package_features || [];
  const included = tx?.package_included_sessions || "";

  if (type === "service") {
    return {
      badge: "Single session",
      badgeIcon: Ticket,
      subtitle: auto
        ? "Your first session is confirmed — see you soon."
        : "Your session credit is ready — pick a time that suits you.",
      whatCard: {
        label: "What you booked",
        title: tx?.package_name || "Session",
        line: `£${Number(tx?.amount || 0).toFixed(0)} · one-off payment`,
        note: "A session credit sits on your account until you use it.",
      },
      showAutoCard: !!auto,
      steps: [
        !auto && { icon: CalendarCheck, title: "Book your session", body: "Pick a time in Appointments — we'll confirm within a working day.", to: "/app/appointments", testId: "ck-step-book" },
        { icon: ClipboardList, title: "Complete your intake", body: "A quick 3-minute questionnaire so we know your history and goals before we meet.", to: "/app/intake", testId: "ck-step-intake" },
        { icon: Sparkles, title: "Prep for your session", body: "Wear comfortable kit and bring a water bottle. If you have recent scans or reports, upload them in your dashboard.", to: "/app", testId: "ck-step-prep" },
        { icon: MessageSquare, title: "Message your clinician", body: "Anything you'd like us to know beforehand? Reply times are usually within a working day.", to: "/app/messages", testId: "ck-step-message" },
      ].filter(Boolean),
    };
  }

  if (type === "membership") {
    const renewsAt = tx?.membership_renews_at
      ? new Date(tx.membership_renews_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
      : null;
    return {
      badge: "Membership · rolling monthly",
      badgeIcon: Star,
      subtitle: "Your membership is live — welcome to the LAB.",
      whatCard: {
        label: "What's included",
        title: tx?.package_name || "Membership",
        line: renewsAt ? `£${Number(tx?.amount || 0).toFixed(0)} / month · renews ${renewsAt}` : `£${Number(tx?.amount || 0).toFixed(0)} / month`,
        note: "Cancel or change anytime from Account → Billing.",
        features: features.slice(0, 6),
      },
      showAutoCard: !!auto,
      steps: [
        { icon: ClipboardList, title: "Complete your intake", body: "A quick 3-minute questionnaire so we know your history and goals before we meet.", to: "/app/intake", testId: "ck-step-intake" },
        !auto && { icon: CalendarCheck, title: "Book your first session", body: "Members get priority slots — pick a time that suits you in Appointments.", to: "/app/appointments", testId: "ck-step-book" },
        { icon: Sparkles, title: "See your programme", body: "Your training and rehab plan appears here as soon as your first assessment is done.", to: "/app/plan", testId: "ck-step-plan" },
        { icon: CreditCard, title: "Manage billing", body: "View invoices, update your card or cancel anytime from your account.", to: "/app/billing", testId: "ck-step-billing" },
      ].filter(Boolean),
    };
  }

  if (type === "online_programme") {
    return {
      badge: "Self-guided programme",
      badgeIcon: Download,
      subtitle: "Your programme is ready — download it and start whenever you're ready.",
      whatCard: {
        label: "What you bought",
        title: tx?.package_name || "Online programme",
        line: `£${Number(tx?.amount || 0).toFixed(0)} · lifetime access`,
        note: "Available forever from your dashboard under Programmes.",
        features: features.slice(0, 6),
      },
      showAutoCard: false,
      steps: [
        { icon: Download, title: "Download your programme", body: "Grab the PDF from your Programmes tab — a copy is also in your inbox.", to: "/app/programmes", testId: "ck-step-download" },
        { icon: ClipboardList, title: "Start your journal", body: "Log sessions, pain and progress so you can see improvements week by week.", to: "/app/programmes", testId: "ck-step-journal" },
        { icon: BadgeCheck, title: "Add a 1-to-1 session (optional)", body: "Prefer a clinician review? Book a one-off consult anytime from Pricing.", to: "/prices", testId: "ck-step-upgrade" },
        { icon: MessageSquare, title: "Message us", body: "Questions on form or progression? Reply times are usually within a working day.", to: "/app/messages", testId: "ck-step-message" },
      ],
    };
  }

  // default: package (multi-session bundle)
  return {
    badge: "Multi-session package",
    badgeIcon: PackageOpen,
    subtitle: auto
      ? "Your package is active — your first session is booked in."
      : "Your package is active — let's get your first session in the diary.",
    whatCard: {
      label: "What you bought",
      title: tx?.package_name || "Package",
      line: included
        ? `£${Number(tx?.amount || 0).toFixed(0)} · ${included}`
        : `£${Number(tx?.amount || 0).toFixed(0)} · one-off payment`,
      note: "Session credits stay on your account until you use them.",
      features: features.slice(0, 6),
    },
    showAutoCard: !!auto,
    steps: [
      { icon: ClipboardList, title: "Complete your intake", body: "A quick 3-minute questionnaire so we know your history and goals before we meet.", to: "/app/intake", testId: "ck-step-intake" },
      !auto && { icon: CalendarCheck, title: "Book your first session", body: "Pick a time in Appointments — the rest of the package unlocks as you go.", to: "/app/appointments", testId: "ck-step-book" },
      { icon: Sparkles, title: "See your programme", body: "Your training and rehab plan appears here as soon as your first assessment is done.", to: "/app/plan", testId: "ck-step-plan" },
      { icon: MessageSquare, title: "Say hi to your clinician", body: "Fastest way to ask us anything before session one — reply times are usually within a working day.", to: "/app/messages", testId: "ck-step-message" },
    ].filter(Boolean),
  };
}

function PaidLanding({ tx, user, nav }) {
  const content = useMemo(() => buildContent(tx), [tx]);
  const auto = tx?.auto_booked_appointment;
  const firstName = (user?.name || "").split(/\s+/)[0] || "";
  const BadgeIcon = content.badge_icon || content.badgeIcon;
  const isMembership = tx?.package_type === "membership";
  const isProgramme = tx?.package_type === "online_programme";

  return (
    <div className="min-h-screen bg-[#0A0A0A] fl-noise">
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-12 md:pt-20 pb-16 space-y-6">
        <Link to="/app" className="flex justify-center mb-4"><Logo variant="wordmark" size="lg" /></Link>

        {/* Hero */}
        <div className="fl-card p-8 md:p-12 relative overflow-hidden" data-testid="checkout-success">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(255,69,0,0.22), transparent 70%)" }} />
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-400" size={26} />
            </div>
            <div className="fl-label text-[#f47a20] mt-5 inline-flex items-center gap-2">
              <PartyPopper size={14} /> You're in
            </div>
            <h1 className="fl-heading text-4xl md:text-5xl leading-[1.05] mt-2" data-testid="checkout-success-title">
              {firstName ? `Welcome, ${firstName}.` : "Welcome to Functional LAB."}
            </h1>
            <p className="text-base md:text-lg text-zinc-300 mt-4 leading-relaxed">
              {content.subtitle}
            </p>
          </div>
        </div>

        {/* What you bought */}
        <div className="fl-card p-6 md:p-8" data-testid="checkout-what-you-bought">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#f47a20]/10 border border-[#f47a20]/30 flex items-center justify-center shrink-0">
              <BadgeIcon size={18} className="text-[#f47a20]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="fl-label text-[#f47a20]">{content.whatCard.label}</div>
              <div className="fl-heading text-xl md:text-2xl mt-1" data-testid="ck-what-title">{content.whatCard.title}</div>
              <div className="text-sm text-zinc-300 mt-2" data-testid="ck-what-line">{content.whatCard.line}</div>
              {content.whatCard.features && content.whatCard.features.length > 0 && (
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                  {content.whatCard.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                      <span className="leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="text-xs text-zinc-500 mt-4">{content.whatCard.note}</div>
            </div>
          </div>
        </div>

        {/* Auto-booked appointment */}
        {content.showAutoCard && auto && (
          <div className="fl-card p-6 md:p-8 border-[#f47a20]/40"
               style={{ background: "linear-gradient(180deg, rgba(255,69,0,0.06), transparent 70%)" }}
               data-testid="auto-appt">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#f47a20]/15 border border-[#f47a20]/40 flex items-center justify-center shrink-0">
                <CalendarCheck size={18} className="text-[#f47a20]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="fl-label text-[#f47a20]">Your first session is booked</div>
                <div className="fl-heading text-xl md:text-2xl mt-1">{auto.service}</div>
                <div className="text-sm text-zinc-300 mt-2">
                  {new Date(auto.scheduled_at).toLocaleString("en-GB", {
                    weekday: "long", day: "numeric", month: "long",
                    hour: "2-digit", minute: "2-digit",
                  })}
                  {" · "}
                  {auto.mode === "remote"
                    ? <span className="inline-flex items-center gap-1"><Video size={12} /> Tele-rehab</span>
                    : "In person at the clinic"}
                </div>
                <div className="text-xs text-zinc-500 mt-3">Need to reschedule? You can move this from Appointments any time.</div>
              </div>
            </div>
          </div>
        )}

        {/* Next steps checklist */}
        <div className="fl-card p-6 md:p-8" data-testid="checkout-nextsteps">
          <div className="fl-label text-[#f47a20] mb-4">Your next steps</div>
          <ul className="space-y-3">
            {content.steps.map((s) => (
              <NextStep key={s.testId} {...s} />
            ))}
          </ul>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={() => nav(isProgramme ? "/app/programmes" : "/app")} className="fl-btn text-sm" data-testid="ck-continue">
            {isProgramme ? "Download my programme" : "Go to my dashboard"}
          </button>
          <Link
            to={isMembership ? "/app/billing" : (isProgramme ? "/app" : "/app/appointments")}
            className="fl-btn-ghost text-sm"
            data-testid="ck-secondary"
          >
            {isMembership ? "Manage billing" : (isProgramme ? "Go to dashboard" : "Manage appointments")}
          </Link>
          <Link to="/prices" className="text-xs text-zinc-500 hover:text-zinc-300 ml-auto self-center" data-testid="ck-back-prices">
            Back to pricing
          </Link>
        </div>

        <div className="text-[11px] text-zinc-500 text-center pt-2">
          A receipt is on its way to <span className="text-zinc-300">{user?.email}</span>. You can view all invoices under Account → Billing.
        </div>
      </div>
    </div>
  );
}

function NextStep({ icon: Icon, title, body, to, testId }) {
  return (
    <li>
      <Link to={to} data-testid={testId}
            className="block rounded-md border border-white/10 bg-white/[0.02] hover:border-[#f47a20]/40 hover:bg-white/[0.04] p-4 transition-colors">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#f47a20]/10 border border-[#f47a20]/30 flex items-center justify-center shrink-0">
            <Icon size={16} className="text-[#f47a20]" />
          </div>
          <div className="min-w-0">
            <div className="fl-heading text-base leading-tight">{title}</div>
            <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{body}</div>
          </div>
        </div>
      </Link>
    </li>
  );
}
